"use client";

import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
  useSyncExternalStore,
} from "react";

import {
  clearAnalyticsCookies,
  type ConsentChoice,
  parseConsent,
  readConsentCookieValue,
  writeConsentCookie,
} from "@/features/consent/lib/consent";

type ConsentContextValue = {
  /** null = inget val gjort ännu (eller före hydrering). */
  consent: ConsentChoice | null;
  /** false fram till att cookien lästs — bannern ska inte blinka förbi. */
  ready: boolean;
  settingsOpen: boolean;
  saveConsent: (choice: ConsentChoice) => void;
  openSettings: () => void;
  closeSettings: () => void;
};

const ConsentContext = createContext<ConsentContextValue | null>(null);

// Cookien är den enda sanningen; lyssnarna väcks när vi själva skriver den.
const listeners = new Set<() => void>();
const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};
// Servern kan inte läsa besökarens cookie här: null = "inte läst än".
const getServerSnapshot = () => null;

export function ConsentProvider({ children }: { children: ReactNode }) {
  const rawConsent = useSyncExternalStore<string | null>(
    subscribe,
    readConsentCookieValue,
    getServerSnapshot,
  );
  const [settingsOpen, setSettingsOpen] = useState(false);

  const saveConsent = useCallback((choice: ConsentChoice) => {
    writeConsentCookie(choice);
    if (!choice.analytics) clearAnalyticsCookies();
    listeners.forEach((listener) => listener());
    setSettingsOpen(false);
  }, []);

  const value = useMemo<ConsentContextValue>(
    () => ({
      consent: parseConsent(rawConsent),
      ready: rawConsent !== null,
      settingsOpen,
      saveConsent,
      openSettings: () => setSettingsOpen(true),
      closeSettings: () => setSettingsOpen(false),
    }),
    [rawConsent, settingsOpen, saveConsent],
  );

  return <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>;
}

/**
 * null utanför providern — t.ex. 404-sidan, som renderar sidfoten utan
 * (site)-layouten. Anropare döljer då sina samtyckeskontroller.
 */
export function useConsent() {
  return useContext(ConsentContext);
}
